import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';
import { API_BASE_URL, STORAGE_KEYS, API_ENDPOINTS, REQUEST_TIMEOUT, HTTP_STATUS } from '@/config/constants';
import type { ApiResponse, AuthTokens } from '@/types';
import { isLocalRefreshToken } from './localAuthTokens';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT.DEFAULT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management utilities
const getAccessToken = (): string | null => {
  // Check sessionStorage first (for non-remember sessions), then localStorage
  return sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
         localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

const getRefreshToken = (): string | null => {
  // Check sessionStorage first (for non-remember sessions), then localStorage
  return sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
         localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
};

const setTokens = (tokens: AuthTokens): void => {
  const storage = sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ? sessionStorage : localStorage;
  storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
  storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
};

const clearTokens = (): void => {
  // Clear from both storages
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
  sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
};

// All failed requests share one refresh, including its rejection.
let refreshPromise: Promise<AuthTokens> | null = null;
const skipsAuth = (config: InternalAxiosRequestConfig): boolean =>
  config.headers.get('X-Skip-Auth') === 'true';

const refreshSession = (): Promise<AuthTokens> => {
  if (!refreshPromise) {
    const refreshToken = getRefreshToken();
    refreshPromise = (async () => {
      if (!refreshToken) throw new Error('登录已失效，请重新登录');
      const endpoint = isLocalRefreshToken(refreshToken) ? '/api/local-auth/refresh' : API_ENDPOINTS.AUTH.REFRESH;
      const response = await axios.post<ApiResponse<AuthTokens>>(
        `${API_BASE_URL}${endpoint}`, { refresh_token: refreshToken }, { timeout: REQUEST_TIMEOUT.DEFAULT },
      );
      if (!response.data.success || !response.data.data) throw new Error('登录已失效，请重新登录');
      // A logout or another login while the request was in flight must win.
      if (getRefreshToken() !== refreshToken) throw new Error('登录状态已改变');
      setTokens(response.data.data);
      return response.data.data;
    })().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    // Allow callers to skip auth header injection (for API-key flows or unauthenticated endpoints)
    const skipAuth = skipsAuth(config);

    if (token && config.headers && !skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
      console.debug('[API] Added Authorization header');
    }

    // Note: X-User-* headers are no longer sent.
    // Backend now extracts user identity directly from CloudBase access_token.
    // This prevents header spoofing attacks.

    // Add request timestamp for monitoring
    config.metadata = { startTime: new Date().getTime() };

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Calculate response time if metadata exists
    if (response.config.metadata?.startTime) {
      const duration = new Date().getTime() - response.config.metadata.startTime;
      console.debug(`[API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`);
    }

    return response;
  },
  async (error: AxiosError<ApiResponse & { detail?: string }>) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && originalRequest && !originalRequest._retry && !skipsAuth(originalRequest)) {
      originalRequest._retry = true;
      const currentToken = getAccessToken();
      if (currentToken && originalRequest.headers.Authorization !== `Bearer ${currentToken}`) {
        originalRequest.headers.Authorization = `Bearer ${currentToken}`;
        return apiClient(originalRequest);
      }
      const failedRefreshToken = getRefreshToken();
      try {
        const newTokens = await refreshSession();
        originalRequest.headers.Authorization = `Bearer ${newTokens.access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        if (getRefreshToken() === failedRefreshToken) {
          clearTokens();
          window.location.href = '/auth/login?error=token_expired';
        }
        return Promise.reject(refreshError);
      }
    }

    // Handle other error cases
    if (error.response) {
      // Server responded with error status
      const apiError = {
        status: error.response.status,
        message: error.response.data?.detail || error.response.data?.message || error.response.data?.error || 'An error occurred',
        details: error.response.data,
      };

      // Log errors for debugging
      console.error('[API Error]', {
        url: error.config?.url,
        status: apiError.status,
        message: apiError.message,
      });

      return Promise.reject(apiError);
    } else if (error.request) {
      // Request made but no response received
      console.error('[Network Error]', error.message);
      return Promise.reject({
        status: 0,
        message: 'Network error. Please check your connection.',
      });
    } else {
      // Something else happened
      console.error('[Request Error]', error.message);
      return Promise.reject({
        status: 0,
        message: error.message || 'An unexpected error occurred',
      });
    }
  }
);

// Extend AxiosRequestConfig to include metadata
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
  }
}

export { apiClient, setTokens, clearTokens, getAccessToken, getRefreshToken };
export default apiClient;
